/**
 * 基本統計量の計算関数
 * 計算式は docs/systems/keisuke/FORMULAS.md を参照
 */

import { KeisukeAdapterError } from '../keisukeAdapterI18n';
import { tDistributionTwoSidedCritical } from './tTest';

/**
 * 基本統計量の結果
 */
export interface BasicStatisticsResult {
  mean: number; // 平均値
  median: number; // 中央値
  stdDev: number; // 標本標準偏差
  stdError: number; // 標準誤差
  variance: number; // 分散
  min: number; // 最小値
  max: number; // 最大値
  range: number; // 範囲
  q1: number; // 第1四分位数
  q3: number; // 第3四分位数
  iqr: number; // 四分位範囲
  confidenceInterval: {
    lower: number;
    upper: number;
  }; // 95%信頼区間
  n: number; // サンプルサイズ
}

/**
 * 分位数（Hyndman–Fan type 7）。R の quantile(..., type=7) と同じ定義。
 * h = (n-1)p に対し、ソート済み値の線形補間。
 */
function quantileType7(sorted: number[], p: number): number {
  const n = sorted.length;
  if (n === 0) {
    throw new KeisukeAdapterError('emptySortedArray');
  }
  if (n === 1) {
    return sorted[0]!;
  }
  if (p <= 0) {
    return sorted[0]!;
  }
  if (p >= 1) {
    return sorted[n - 1]!;
  }
  const h = (n - 1) * p;
  const lo = Math.floor(h);
  const hi = Math.ceil(h);
  const xLo = sorted[lo]!;
  if (lo === hi) {
    return xLo;
  }
  const xHi = sorted[hi]!;
  return xLo + (h - lo) * (xHi - xLo);
}

/**
 * 数値配列から基本統計量を計算
 */
export function calculateBasicStatistics(data: number[]): BasicStatisticsResult {
  if (data.length === 0) {
    throw new KeisukeAdapterError('emptyData');
  }

  // 数値データのみを抽出
  const numericData = data.filter((v) => typeof v === 'number' && !isNaN(v) && isFinite(v));
  
  if (numericData.length === 0) {
    throw new KeisukeAdapterError('invalidNumericData');
  }

  const n = numericData.length;

  if (n === 1) {
    const v = numericData[0]!;
    return {
      mean: v,
      median: v,
      stdDev: 0,
      stdError: 0,
      variance: 0,
      min: v,
      max: v,
      range: 0,
      q1: v,
      q3: v,
      iqr: 0,
      confidenceInterval: { lower: v, upper: v },
      n: 1,
    };
  }

  // 平均値
  const mean = numericData.reduce((sum, val) => sum + val, 0) / n;

  // 分散と標準偏差
  const variance = numericData.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / (n - 1);
  const stdDev = Math.sqrt(variance);
  
  // 標準誤差
  const stdError = stdDev / Math.sqrt(n);
  
  // 最小値・最大値・範囲
  const sorted = [...numericData].sort((a, b) => a - b);
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const range = max - min;
  
  // 中央値・四分位数（type 7、R quantile 既定と整合）
  const median = quantileType7(sorted, 0.5);
  const q1 = quantileType7(sorted, 0.25);
  const q3 = quantileType7(sorted, 0.75);
  const iqr = q3 - q1;
  
  // 95%信頼区間（t 検定と同じ不完全ベータ経路の臨界値）
  const tValue = tDistributionTwoSidedCritical(n - 1, 0.05);
  const margin = tValue * stdError;
  const confidenceInterval = {
    lower: mean - margin,
    upper: mean + margin,
  };
  
  return {
    mean,
    median,
    stdDev,
    stdError,
    variance,
    min,
    max,
    range,
    q1,
    q3,
    iqr,
    confidenceInterval,
    n,
  };
}

